<template>
    <MainContent title="Configurar invitaciones de abonados">
        <template #header>
            <h5 class="tw:text-sm tw:text-gray-500 tw:pb-2">
                Opciones para configurar las invitaciones de abonados para este evento
            </h5>
        </template>

        <ValidationObserver ref="observer" tag="form" @submit.prevent="submit(observer)">

            <!-- Loading inicial -->
            <LoadingSpinner v-if="!evento" />

            <template v-else>
                <div class="tw:space-y-6">

                    <Card title="Configuración de invitaciones">
                        <div class="tw:grid tw:grid-cols-1 tw:gap-6 md:tw:grid-cols-2">
                            <!-- Número máximo de invitaciones -->
                            <BaseInputNumber
                                v-model="evento.n_invitaciones_abonados"
                                id="n_invitaciones_abonados"
                                name="número máximo de invitaciones por abonado"
                                label="Número máximo de invitaciones que puede sacar un abonado"
                                rules="required"
                                :min="0"
                                :disabled="loading"
                            />

                            <!-- Restricción de invitaciones -->
                            <div>
                                <BaseMultiselect
                                    v-model="evento.tipo_invitaciones_abonados"
                                    name="restricción de invitaciones"
                                    :rules="reglasRestriccion"
                                    label="Restricción para la selección de invitaciones del abonado"
                                    :options="opcionesTipoInvitacionesAbonados"
                                    value-key="value"
                                    label-key="text"
                                    :disabled="loading"
                                    @input="resetData"
                                />
                                <p class="tw:mt-2 tw:text-xs tw:text-gray-400">
                                    En este selector podrá configurar en qué lugar el abonado podrá adquirir sus invitaciones.
                                </p>
                            </div>
                        </div>

                        <!-- Botón Guardar -->
                        <div class="tw:flex tw:justify-end tw:pt-2">
                            <Button type="submit" :disabled="loading || !puedeGuardar" class="tw:ml-auto">
                                Guardar
                            </Button>
                        </div>
                    </Card>

                    <!-- Zonas -->
                    <template v-if="zonas.length">
                        <hr class="tw:border-gray-200" />

                        <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
                            <h6 class="tw:text-sm tw:font-semibold tw:text-gray-700">
                                Zonas disponibles
                                <span class="tw:font-normal tw:text-gray-400 tw:ml-1">
                                    — Selecciona en qué zonas el abonado podrá adquirir invitaciones
                                </span>
                            </h6>
                            <div class="tw:flex tw:items-center tw:gap-2">
                                <label class="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-600 tw:cursor-pointer tw:select-none" for="marcar_en_todos">
                                    <input
                                        type="checkbox"
                                        :checked="marcarEnTodos"
                                        id="marcar_en_todos"
                                        class="tw:appearance-auto tw:h-4 tw:w-4 tw:shrink-0 tw:rounded tw:border-gray-300 tw:text-blue-600 focus:tw:ring-blue-500"
                                        :disabled="loading"
                                        @change="marcarTodasLasZonas($event.target.checked)"
                                    />
                                    MARCAR EN TODOS
                                </label>
                            </div>
                        </div>

                        <div class="tw:max-w-xs tw:mb-4">
                            <SearchComponent v-model="search" :disabled="loading" hide-label placeholder="Buscar zona..." />
                        </div>
                    </template>

                    <!-- Lista de zonas con restricción tipo 1 -->
                    <div v-if="esTipoRestriccionUno" class="tw:max-h-[calc(100svh-525px)] tw:overflow-y-auto tw:space-y-2">
                        <LoadingSpinner v-if="loadingZonas" />

                        <template v-else>
                            <div
                                v-for="(zona, index) in zonasFiltradas"
                                :key="index"
                                class="tw:flex tw:items-center tw:justify-between tw:p-3 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white hover:tw:bg-gray-50 tw:transition-colors"
                            >
                                <span class="tw:text-sm tw:font-medium tw:text-gray-800">{{ zona.name }}</span>
                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <label class="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-600 tw:cursor-pointer tw:select-none" :for="'taquilla-' + zona.id">
                                        <input
                                            v-model="zona.invitaciones_abonado.taquilla"
                                            type="checkbox"
                                            class="tw:appearance-auto tw:h-4 tw:w-4 tw:shrink-0 tw:rounded tw:border-gray-300 tw:text-blue-600 focus:tw:ring-blue-500"
                                            :id="'taquilla-' + zona.id"
                                            :disabled="loading"
                                            @change="syncChange($event, 'taquilla', zona)"
                                        />
                                        Taquilla
                                    </label>
                                    <label class="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-600 tw:cursor-pointer tw:select-none" :for="'online-' + zona.id">
                                        <input
                                            v-model="zona.invitaciones_abonado.online"
                                            type="checkbox"
                                            class="tw:appearance-auto tw:h-4 tw:w-4 tw:shrink-0 tw:rounded tw:border-gray-300 tw:text-blue-600 focus:tw:ring-blue-500"
                                            :id="'online-' + zona.id"
                                            :disabled="loading"
                                            @change="syncChange($event, 'online', zona)"
                                        />
                                        Online
                                    </label>
                                </div>
                            </div>

                            <div v-if="!zonasFiltradas.length" class="tw:mt-3">
                                <h4 class="tw:text-base tw:text-gray-500">
                                    {{ zonas.length ? 'Ninguna zona coincide con la búsqueda' : 'No hay ninguna zona disponible' }}
                                </h4>
                            </div>
                        </template>
                    </div>

                    <!-- Errores del servidor -->
                    <div v-if="errors.length" class="tw:rounded-md tw:bg-red-50 tw:border tw:border-red-200 tw:p-4">
                        <div v-for="(error, idx) in errors" :key="idx" class="tw:text-sm tw:text-red-700">
                            {{ idx + 1 }}. {{ error }}
                        </div>
                    </div>

                </div>
            </template>
        </ValidationObserver>
    </MainContent>
</template>

<script setup>
    import { ref } from 'vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import LoadingSpinner from '@/modules/admin/ui/LoadingSpinner.vue'
    import { useInvitacionesAbonados } from '@/composables/admin/events/useInvitacionesAbonados'

    const props = defineProps({
        evento_id: {
            type: Number,
            required: true
        }
    })

    const observer = ref(null)

    const {
        zonas,
        search,
        errors,
        evento,
        loading,
        loadingZonas,
        marcarEnTodos,
        opcionesTipoInvitacionesAbonados,
        esMovil,
        reglasRestriccion,
        esTipoRestriccionUno,
        zonasFiltradas,
        puedeGuardar,
        syncChange,
        marcarTodasLasZonas,
        resetData,
        submit
    } = useInvitacionesAbonados(props.evento_id)
</script>
